import { useEffect, useRef, useState } from "react";
import { ScrubField } from "./ScrubField";
import { ScrubInput } from "./ScrubInput";
import { formatNumberInput, parseNumericInput } from "./numberFormatting";

export function Metric({
  label,
  value,
  disabled,
  min,
  max,
  step = 1,
  compact = false,
  onCommit,
}: {
  label: string;
  value?: number;
  disabled: boolean;
  min?: number;
  max?: number;
  step?: number;
  compact?: boolean;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState("");
  const isScrubbingRef = useRef(false);
  const onCommitRef = useRef(onCommit);

  onCommitRef.current = onCommit;

  useEffect(() => {
    if (!isScrubbingRef.current) {
      setDraft(value === undefined ? "" : formatNumberInput(value));
    }
  }, [value]);

  function commit(): void {
    const parsed = parseNumericInput(draft);
    if (parsed !== undefined) {
      const clamped = clampValue(parsed, min, max);
      setDraft(formatNumberInput(clamped));
      onCommitRef.current(clamped);
    }
  }

  function updateValue(nextValue: number): void {
    const clamped = clampValue(nextValue, min, max);
    setDraft(formatNumberInput(clamped));
    onCommitRef.current(clamped);
  }

  function endScrub(): void {
    isScrubbingRef.current = false;
  }

  return (
    <ScrubField
      label={label}
      scrubValue={parseNumericInput(draft)}
      scrubMin={min}
      scrubMax={max}
      scrubStep={step}
      scrubDisabled={disabled}
      onScrubChange={updateValue}
      onScrubStart={() => {
        isScrubbingRef.current = true;
      }}
      onScrubEnd={endScrub}
      className={compact ? "w-20" : undefined}
    >
      <ScrubInput
        type="text"
        inputMode="decimal"
        data-numeric
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        value={draft}
        onChange={event => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key === "Enter") {
            event.currentTarget.blur();
            return;
          }
          if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            // Commit immediately so inspector nudges apply without requiring Enter/blur.
            event.preventDefault();
            const current = parseNumericInput(draft);
            const fallback = event.key === "ArrowUp" ? (min ?? 0) : min ?? 0;
            const direction = event.key === "ArrowUp" ? 1 : -1;
            updateValue((current !== undefined ? current : fallback) + direction * step);
          }
        }}
      />
    </ScrubField>
  );
}

function clampValue(value: number, min: number | undefined, max: number | undefined): number {
  const withMin = min === undefined ? value : Math.max(min, value);
  return max === undefined ? withMin : Math.min(max, withMin);
}
